import { Divider, Grid } from '@mui/material';
import { EventBlock } from '@/components/home/event/EventBlock';
import { LetterCountDTO } from '@/types/letter/LetterCountDTO';
import { NavigationBlock } from '@/components/home/NavigationBlock';
import { Outlet, useOutletContext } from 'react-router-dom';
import { SenderInfoDTO } from '@/types/create/SenderInfoDTO';
import { SystemEventDTO } from '@/types/event/system/SystemEventDTO';
import { UserDTO } from '@/types/user/UserDTO';
import { convertSystemEvent } from '@/utils/event-utils';
import { getCurrentUser } from '@/services/authSlice';
import { useAppSelector } from '@/app/hooks';
import { useGetNewsQuery, useGetSystemEventsQuery } from '@/services/api/eventApiSlice';
import React, { FC, ReactElement } from 'react';

interface OutletProps {
  inboxCount?: LetterCountDTO;
  outboxCount?: LetterCountDTO;
  senders?: SenderInfoDTO[];
  letterDraftsCount?: number;
  isLoadingSenders?: boolean;
}

export const HomePage: FC = (): ReactElement => {
  const { inboxCount, outboxCount, senders, letterDraftsCount, isLoadingSenders } = useOutletContext<OutletProps>();

  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);

  const {
    data: events,
    isLoading,
    isFetching,
  } = useGetSystemEventsQuery(authUser?.id, {
    skip: !authUser,
  });

  const { data: news } = useGetNewsQuery();

  const allEvents: SystemEventDTO[] = [...(events || []), ...(news || [])]
    .sort((e1: SystemEventDTO, e2: SystemEventDTO) => {
      const date1: number = e1.timestamp ? new Date(e1.timestamp).getTime() : 0;
      const date2: number = e2.timestamp ? new Date(e2.timestamp).getTime() : 0;
      return date2 - date1;
    })
    .slice(0, 3);

  const loading: boolean = isLoading || isFetching;

  const isSearchFieldEnable = !!(senders && senders.length > 1);

  return (
    <Grid container={true} direction={'row'} spacing={7}>
      <Grid item={true} xs={3}>
        <Grid container={true} direction={'column'} spacing={3}>
          <Grid item={true}>
            <NavigationBlock
              inboxCount={inboxCount}
              outboxCount={outboxCount}
              senders={senders}
              letterDraftsCount={letterDraftsCount}
              isLoadingSenders={isLoadingSenders}
            />
          </Grid>
          <Grid item={true}>
            <Divider />
          </Grid>
          <Grid item={true}>
            <EventBlock
              events={(allEvents || []).map((event: SystemEventDTO) => convertSystemEvent(event))}
              isLoading={loading}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item={true} xs={true}>
        <Outlet context={{ isSearchFieldEnable }} />
      </Grid>
    </Grid>
  );
};
